// client/src/utils/tabSession.ts (converted from .js, ST-00230)
//
// Persists the open tab set so relaunching the installed app puts you back where
// you were, rather than on an empty dashboard. Kept per signed-in user since PLT-20
// (utils/userStorage): on a shared PC the next person never gets someone else's tabs.

import { safeStorage, userStorageKey } from './userStorage';

const STORAGE_KEY = 'lb-tab-session';
// Older than this and the set is more likely stale than useful — a Monday
// morning shouldn't restore Friday afternoon's working set.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_TABS = 20;

/** One remembered tab. */
export interface SavedTab {
  url: string;
  pinned: boolean;
}

/** The remembered set, as loadTabSession hands it back. */
export interface TabSession {
  tabs: SavedTab[];
  activeIndex: number;
}

/** What saveTabSession takes: the live tabs (extra fields are not stored). */
export interface TabSessionInput {
  tabs: ReadonlyArray<{ url: string; pinned?: boolean }>;
  activeIndex: number;
}

/** The stored JSON, unchecked. */
interface StoredTabSession {
  tabs?: unknown;
  activeIndex?: unknown;
  savedAt?: unknown;
}

const isSavedTabLike = (t: unknown): t is { url: string; pinned?: unknown } =>
  !!t &&
  typeof t === 'object' &&
  typeof (t as { url?: unknown }).url === 'string' &&
  (t as { url: string }).url.startsWith('/');

export function loadTabSession(): TabSession | null {
  // The set saved before it was kept per user belongs to whoever used the PC last.
  safeStorage.remove(STORAGE_KEY);
  const key = userStorageKey(STORAGE_KEY);
  if (!key) return null;
  try {
    const raw = safeStorage.get(key);
    if (!raw) return null;

    const data = JSON.parse(raw) as StoredTabSession | null;
    if (!data || !Array.isArray(data.tabs) || data.tabs.length === 0) return null;
    if (
      typeof data.savedAt !== 'number' ||
      !data.savedAt ||
      Date.now() - data.savedAt > MAX_AGE_MS
    ) {
      return null;
    }

    const tabs = (data.tabs as unknown[])
      .filter(isSavedTabLike)
      .slice(0, MAX_TABS)
      .map((t) => ({ url: t.url, pinned: !!t.pinned }));

    if (!tabs.length) return null;
    return {
      tabs,
      activeIndex: Number.isInteger(data.activeIndex) ? (data.activeIndex as number) : 0,
    };
  } catch {
    return null;
  }
}

export function saveTabSession({ tabs, activeIndex }: TabSessionInput): void {
  const key = userStorageKey(STORAGE_KEY);
  if (!key) return;
  safeStorage.set(
    key,
    JSON.stringify({
      tabs: tabs.slice(0, MAX_TABS).map((t) => ({ url: t.url, pinned: !!t.pinned })),
      activeIndex,
      savedAt: Date.now(),
    })
  );
}

/** Forget the signed-in user's set (logout), and any set from before it was kept per user. */
export function clearTabSession(): void {
  safeStorage.remove(STORAGE_KEY);
  const key = userStorageKey(STORAGE_KEY);
  if (key) safeStorage.remove(key);
}
